import React, { useEffect, useState, useMemo } from "react";
import ResponsiveImg, { SIZES } from "../components/ResponsiveImg";
import Link from "../components/LocalizedLink";
import { useI18n } from "../i18n/I18nProvider";
import { ArrowRight, ArrowDown } from "lucide-react";
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from "../components/ui/accordion";
import HeroMedia, { HERO_BG } from "../components/HeroMedia";
import DevelopmentCard from "../components/DevelopmentCard";
import ContactForm from "../components/ContactForm";
import SchemaMarkup, { buildFAQ, buildBreadcrumb, buildOrganization, buildWebsite } from "../components/SchemaMarkup";
import useDocumentMeta from "../hooks/useDocumentMeta";
import { api, resolveImage } from "../lib/api";
import { lifestylePillars } from "../data/content";

const FALLBACK = {
  eyebrow: "Casares · Costa del Sol · Spain",
  heading: "Luxury New Developments\nin Finca Cortesín Resort",
  hero_image: "",
  hero_subtitle:
    "Discover exclusive apartments, penthouses and villas in one of the Costa del Sol's most prestigious golf and lifestyle resorts.",
  intro_eyebrow: "The Resort",
  intro_heading: "The essence of <em>Mediterranean</em> luxury, quietly held inside 215 hectares.",
  intro: "",
  stats: [],
  why_buy_heading: "Six reasons buyers commit to Finca Cortesín",
  why_buy: [],
  buyer_guide_heading: "Buying off-plan in Spain, <em>simplified</em>.",
  buyer_guide: [],
  faqs: [],
  contact_eyebrow: "Speak With Us",
  contact_heading: "Receive the updated price list and floor plans.",
  contact_intro: "",
  seo: {
    title: "New Developments in Finca Cortesin, Casares | Luxury Golf Apartments & Villas",
    description:
      "Discover 11 luxury new developments in Finca Cortesin, Casares.",
  },
};

const renderHTML = (html) => ({ __html: html || "" });

export default function Home() {
  const [page, setPage] = useState(FALLBACK);
  const [developments, setDevelopments] = useState([]);
  const { t } = useI18n();
  const pillars = lifestylePillars.map((p) => ({ ...p, title: t(`pillar_${p.key}_title`), body: t(`pillar_${p.key}_body`) }));

  useEffect(() => {
    (async () => {
      try {
        const [{ data: p }, { data: devs }] = await Promise.all([
          api.get("/pages/home"),
          api.get("/developments"),
        ]);
        setPage({ ...FALLBACK, ...p });
        setDevelopments(devs);
      } catch (_err) { /* ignore */ }
    })();
  }, []);

  useDocumentMeta({
    title: page.seo?.title,
    description: page.seo?.description,
    image: page.hero_image || "",
  });

  const headingLines = useMemo(() => (page.heading || "").split("\n"), [page.heading]);

  return (
    <div className="bg-[#F9F6F0] text-[#1A1A1A]">
      <SchemaMarkup schema={buildFAQ(page.faqs || [])} />
      <SchemaMarkup schema={buildBreadcrumb([{ name: t("home"), url: "/" }, { name: t("developments"), url: "/developments" }])} />
      <SchemaMarkup schema={buildOrganization()} />
      <SchemaMarkup schema={buildWebsite()} />

      {/* HERO */}
      <section className={`relative h-[100svh] min-h-[640px] w-full overflow-hidden ${HERO_BG}`}>
        <HeroMedia src={page.hero_image ? resolveImage(page.hero_image) : ""} alt={t("hero_image_alt")} overlay="bg-gradient-to-b from-black/15 via-black/5 to-black/30" />
        <div className="relative z-10 h-full flex flex-col justify-end max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16 pb-20 lg:pb-28">
          <p data-testid="hero-eyebrow" className="text-[10px] tracking-[0.4em] uppercase text-[#F9F6F0]/80 mb-6">{page.eyebrow}</p>
          <h1 data-testid="hero-title" className="text-5xl sm:text-6xl lg:text-7xl xl:text-8xl text-[#F9F6F0] font-light leading-[0.95] tracking-tight max-w-5xl" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            {headingLines.map((line, i) => (
              <React.Fragment key={i}>
                {i > 0 && <br />}
                {i === headingLines.length - 1 && headingLines.length > 1 ? (
                  <em className="not-italic text-[#E5C99A]">{line}</em>
                ) : (
                  line
                )}
              </React.Fragment>
            ))}
          </h1>
          <p className="text-base lg:text-lg text-[#F9F6F0]/85 mt-8 max-w-2xl leading-relaxed">{page.hero_subtitle}</p>
          <div className="flex flex-wrap gap-3 mt-12">
            <Link to="/developments" data-testid="hero-cta-view" className="bg-[#C5A880] text-[#1A1A1A] px-8 py-4 text-[11px] tracking-[0.25em] uppercase hover:bg-[#F9F6F0] transition-colors duration-300 inline-flex items-center gap-3">
              {t("view_developments")} <ArrowRight className="w-3.5 h-3.5" strokeWidth={1.5} />
            </Link>
            <Link to="/contact?request=consultation" data-testid="hero-cta-consult" className="text-[#F9F6F0] px-2 py-4 text-[11px] tracking-[0.25em] uppercase border-b border-[#F9F6F0]/40 hover:border-[#C5A880] hover:text-[#C5A880] transition-colors">
              {t("book_consultation")}
            </Link>
          </div>
        </div>
        <div className="absolute left-6 md:left-12 lg:left-16 bottom-8 z-10 flex items-center gap-3 text-[#F9F6F0]/70 text-[10px] tracking-[0.3em] uppercase">
          <ArrowDown className="w-3.5 h-3.5 animate-bounce" strokeWidth={1.5} />
          <span>{t("scroll")}</span>
        </div>
      </section>

      {/* INTRO */}
      <section className="py-24 lg:py-36">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
          <div className="lg:col-span-5">
            <p className="text-[10px] tracking-[0.3em] uppercase text-[#73706B] mb-6">— {page.intro_eyebrow}</p>
            <h2 className="text-4xl lg:text-5xl xl:text-6xl font-light leading-[1.05]" style={{ fontFamily: "'Cormorant Garamond', serif" }} dangerouslySetInnerHTML={renderHTML(page.intro_heading)} />
          </div>
          <div className="lg:col-span-6 lg:col-start-7 space-y-6 text-[#3D3B38] leading-relaxed">
            <div className="prose prose-stone max-w-none text-lg" dangerouslySetInnerHTML={renderHTML(page.intro)} />
            {(page.stats || []).length > 0 && (
              <div className="flex flex-wrap gap-8 pt-4">
                {page.stats.map((s, i) => (
                  <div key={i}>
                    <p className="text-4xl font-light" style={{ fontFamily: "'Cormorant Garamond', serif" }}>{s.k}</p>
                    <p className="text-[10px] tracking-[0.3em] uppercase text-[#73706B] mt-1">{s.l}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* WHY BUY */}
      {(page.why_buy || []).length > 0 && (
        <section className="py-24 lg:py-32 bg-[#FFFFFF] border-y border-[#E5DFD5]">
          <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16">
            <div className="mb-16">
              <p className="text-[10px] tracking-[0.3em] uppercase text-[#73706B] mb-4">— {t("home_why_buy_eyebrow")}</p>
              <h2 className="text-4xl lg:text-5xl font-light leading-tight max-w-3xl" style={{ fontFamily: "'Cormorant Garamond', serif" }}>{page.why_buy_heading}</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-12 gap-y-12">
              {page.why_buy.map((p, i) => (
                <div key={i} className="flex flex-col">
                  <span className="text-3xl text-[#C5A880] mb-4 font-light" style={{ fontFamily: "'Cormorant Garamond', serif" }}>{String(i + 1).padStart(2, "0")}</span>
                  <h3 className="text-2xl font-light mb-3" style={{ fontFamily: "'Cormorant Garamond', serif" }}>{p.title}</h3>
                  <p className="text-base text-[#73706B] leading-relaxed">{p.body}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* DEVELOPMENTS */}
      <section id="developments" className="py-24 lg:py-32">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16">
          <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-8 mb-16">
            <div>
              <p className="text-[10px] tracking-[0.3em] uppercase text-[#73706B] mb-4">— {t("home_collection_eyebrow")}</p>
              <h2 className="text-4xl lg:text-5xl font-light leading-tight" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                {t("home_collection_heading", { count: developments.length || 11 })}<br />
                <em className="not-italic text-[#C5A880]">{t("home_collection_heading_em")}</em>.
              </h2>
            </div>
            <Link to="/developments" data-testid="developments-view-all" className="text-[11px] tracking-[0.25em] uppercase border-b border-[#1A1A1A] pb-1 hover:text-[#C5A880] hover:border-[#C5A880] transition-colors self-start lg:self-end">
              {t("view_all_developments")}
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-16">
            {developments.map((d, i) => (<DevelopmentCard key={d.slug} d={d} index={i} />))}
          </div>
        </div>
      </section>

      {/* LIFESTYLE */}
      <section className="py-24 lg:py-32 bg-[#FFFFFF] border-y border-[#E5DFD5]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16">
          <p className="text-[10px] tracking-[0.3em] uppercase text-[#73706B] mb-4">— {t("home_lifestyle_eyebrow")}</p>
          <h2 className="text-4xl lg:text-5xl font-light max-w-3xl mb-16" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            {t("home_lifestyle_heading")}
          </h2>
          <div className="grid grid-cols-12 grid-rows-2 gap-4 lg:gap-6 auto-rows-fr">
            <Link to="/explore/golf-course" className="col-span-12 md:col-span-6 row-span-2 relative overflow-hidden aspect-[4/5] md:aspect-auto group">
              <ResponsiveImg src={pillars[0].image} sizes={SIZES.half} alt={pillars[0].title} className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1500ms] group-hover:scale-[1.04]" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
              <div className="relative z-10 h-full flex flex-col justify-end p-8 lg:p-12 text-[#F9F6F0]">
                <p className="text-[10px] tracking-[0.3em] uppercase text-[#E5C99A] mb-3">{t("home_lifestyle_first_tag")}</p>
                <h3 className="text-3xl lg:text-5xl font-light leading-tight mb-3" style={{ fontFamily: "'Cormorant Garamond', serif" }}>{pillars[0].title}</h3>
                <p className="text-base text-[#F9F6F0]/85 max-w-md">{pillars[0].body}</p>
              </div>
            </Link>
            {[
              { p: pillars[1], to: "/explore/spa" },
              { p: pillars[2], to: "/explore/beach-club" },
            ].map(({ p, to }, i) => (
              <Link key={p.key} to={to} className="col-span-12 md:col-span-3 row-span-1 relative overflow-hidden aspect-[4/3] md:aspect-auto group">
                <ResponsiveImg src={p.image} sizes="(min-width: 768px) 25vw, 100vw" alt={p.title} className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1500ms] group-hover:scale-[1.04]" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/65 to-transparent" />
                <div className="relative z-10 h-full flex flex-col justify-end p-6 lg:p-8 text-[#F9F6F0]">
                  <p className="text-[10px] tracking-[0.3em] uppercase text-[#E5C99A] mb-2">0{i + 2}</p>
                  <h3 className="text-xl lg:text-2xl font-light" style={{ fontFamily: "'Cormorant Garamond', serif" }}>{p.title}</h3>
                </div>
              </Link>
            ))}
            {[
              { p: pillars[3], to: "/explore/restaurants" },
              { p: pillars[4], to: "/explore/hotel" },
              { p: pillars[5], to: "/about" },
            ].map(({ p, to }, i) => (
              <Link key={p.key} to={to} className="col-span-12 md:col-span-2 row-span-1 relative overflow-hidden aspect-[4/3] md:aspect-auto group">
                <ResponsiveImg src={p.image} sizes="(min-width: 768px) 17vw, 100vw" alt={p.title} className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1500ms] group-hover:scale-[1.04]" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
                <div className="relative z-10 h-full flex flex-col justify-end p-5 lg:p-6 text-[#F9F6F0]">
                  <p className="text-[9px] tracking-[0.3em] uppercase text-[#E5C99A] mb-1">0{i + 4}</p>
                  <h3 className="text-lg lg:text-xl font-light" style={{ fontFamily: "'Cormorant Garamond', serif" }}>{p.title}</h3>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* BUYER GUIDE */}
      {(page.buyer_guide || []).length > 0 && (
        <section className="py-24 lg:py-32 bg-[#1A1A1A] text-[#F9F6F0]">
          <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
              <div className="lg:col-span-5">
                <p className="text-[10px] tracking-[0.3em] uppercase text-[#C5A880] mb-4">— {t("home_buyer_guide_eyebrow")}</p>
                <h2 className="text-4xl lg:text-5xl xl:text-6xl font-light leading-[1.05]" style={{ fontFamily: "'Cormorant Garamond', serif" }} dangerouslySetInnerHTML={renderHTML(page.buyer_guide_heading)} />
                <Link to="/blog/buying-off-plan-property-spain-guide" data-testid="buyer-guide-link" className="inline-block mt-8 text-[11px] tracking-[0.25em] uppercase border-b border-[#C5A880] pb-1 text-[#C5A880] hover:text-[#F9F6F0] hover:border-[#F9F6F0] transition-colors">
                  {t("home_read_guide")}
                </Link>
              </div>
              <div className="lg:col-span-7 space-y-10">
                {page.buyer_guide.map((s) => (
                  <div key={s.number} className="grid grid-cols-12 gap-6 border-b border-[#333333] pb-8">
                    <div className="col-span-2"><span className="text-3xl lg:text-4xl text-[#C5A880] font-light" style={{ fontFamily: "'Cormorant Garamond', serif" }}>{s.number}</span></div>
                    <div className="col-span-10">
                      <h3 className="text-2xl font-light mb-2" style={{ fontFamily: "'Cormorant Garamond', serif" }}>{s.title}</h3>
                      <p className="text-base text-[#A8A39A] leading-relaxed">{s.body}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* FAQ */}
      {(page.faqs || []).length > 0 && (
        <section className="py-24 lg:py-32 bg-[#FFFFFF]">
          <div className="max-w-[1100px] mx-auto px-6 md:px-12 lg:px-16">
            <p className="text-[10px] tracking-[0.3em] uppercase text-[#73706B] mb-4">— {t("frequently_asked")}</p>
            <h2 className="text-4xl lg:text-5xl font-light mb-12" style={{ fontFamily: "'Cormorant Garamond', serif" }}>{t("home_faq_heading")}</h2>
            <Accordion type="single" collapsible className="w-full" data-testid="home-faq">
              {page.faqs.map((f, i) => (
                <AccordionItem value={`faq-${i}`} key={i} className="border-b border-[#E5DFD5]">
                  <AccordionTrigger data-testid={`faq-trigger-${i}`} className="text-left text-lg lg:text-xl py-6 hover:text-[#C5A880] hover:no-underline" style={{ fontFamily: "'Cormorant Garamond', serif" }}>{f.q}</AccordionTrigger>
                  <AccordionContent className="text-base text-[#73706B] leading-relaxed pb-6">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </section>
      )}

      {/* CONTACT */}
      <section id="contact" className="py-24 lg:py-32 bg-[#F9F6F0]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
          <div className="lg:col-span-5">
            <p className="text-[10px] tracking-[0.3em] uppercase text-[#73706B] mb-4">— {page.contact_eyebrow}</p>
            <h2 className="text-4xl lg:text-5xl xl:text-6xl font-light leading-[1.05]" style={{ fontFamily: "'Cormorant Garamond', serif" }} dangerouslySetInnerHTML={renderHTML(page.contact_heading)} />
            <div className="prose prose-stone max-w-none text-sm text-[#73706B] mt-6 leading-relaxed max-w-md" dangerouslySetInnerHTML={renderHTML(page.contact_intro)} />
          </div>
          <div className="lg:col-span-7">
            <ContactForm testIdPrefix="home-contact" sourcePage="Home page" />
          </div>
        </div>
      </section>
    </div>
  );
}
